import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ProjectIssuanceConstants, UserType } from "../../../constants";
import { getProjectPhaseCounts } from "../../../constants/project-phase";
import { TaskConstants } from "../../../constants/workflow/TaskConstants";
import {
  CursorChangeProps,
  FilterOperator,
  InventoryBreakdownMeasurement,
  ResultType,
  UpcomingMilestonePropsWithKey,
} from "../../../models";
import {
  getAggregateIssuances,
  getProjectAggregate,
  GetTaskBoardResponse,
  searchWorkflowTasks,
  SearchWorkflowTasksResponse,
} from "../../../service/query";
import { ResultData, Status } from "../../../service/Shared";
import { useTaskPanelWorkflow } from "../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../useAuth";
import { buildInventoryBreakdownMeasurements, flattenObject, getTaskParentObjectNavigation } from "../../../utils";
import {
  ChartData,
  DashboardMapPin,
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  dataGridMapFilterCriteria,
  Toast,
  useProjectMapPins,
} from "../../../widget";

interface UseDashboardReturnData {
  phaseChartData: ChartData;
  isLoading: boolean;
  currentUserType: UserType;
  unitMeasurementTotals: InventoryBreakdownMeasurement[];
  isLoadingUnitMeasurementTotals: boolean;
  selectedStatuses: string[];
  setSelectedStatuses: (statuses: string[]) => void;

  isLoadingTasks: boolean;
  columnsTasks: DataGridColumnDefinition[];
  tasksDataLoad: DataGridCursorDataLoadEventHandler;
  upcomingMilestones: UpcomingMilestonePropsWithKey[];
  isLoadingMilestones: boolean;
  hasMilestonesError: boolean;
  isUpcomingMilestonesPanelOpen: boolean;
  setIsUpcomingMilestonesPanelOpen: (isOpen: boolean) => void;
  isYourTasksPanelOpen: boolean;
  setIsYourTasksPanelOpen: (isOpen: boolean) => void;
  isTasks: boolean;
  projectPins: DashboardMapPin[];
  isLoadingProjectPins: boolean;
  isMapExpanded: boolean;
  setIsMapExpanded: (isExpanded: boolean) => void;
  workflow: GetTaskBoardResponse["results"][number] | undefined;
  selectedPanelTask: SearchWorkflowTasksResponse["results"][0] | undefined;
  showWorkflowTaskPanel: boolean;
  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void;
  setShowWorkflowTaskPanel: (show: boolean) => void;
  handleCloseWorkflowTaskPanel: () => void;
  handleRefresh: () => void;
  refreshTasks: boolean;
}

const columnsTasks: DataGridColumnDefinition[] = [
  {
    // The task name opens the task panel, which is what the removed "View" icon column used to do.
    key: "taskLink",
    name: "Task",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    minWidth: 200,
  },
  {
    // The uniform parent (Project / Deal / Landholding), which a general task has too.
    key: "parentObjectLink",
    name: "Project / Landholding",
    dataType: "string",
    formatter: "buttonLink",
    alignment: "left",
    minWidth: 140,
  },
  {
    key: "status",
    name: "Status",
    dataType: "string",
    formatter: "stringWithEllipsis",
    alignment: "left",
  },
  {
    key: "deadlineDate",
    name: "Deadline",
    dataType: "Date",
    formatter: "dateOnlyWithIndicator",
    alignment: "left",
  },
];

// Fixed keys so a repeatedly failing fetch replaces its own toast rather than stacking
const LOAD_ERROR_TOASTS = {
  projectOverview: {
    key: "developer-dashboard-project-overview-load-error",
    message: "Failed to load your project overview",
  },
  milestones: {
    key: "developer-dashboard-milestones-load-error",
    message: "Failed to load upcoming milestones",
  },
  taskBoard: {
    key: "developer-dashboard-task-board-load-error",
    message: "Failed to load the task board for this task",
  },
  tasks: {
    key: "developer-dashboard-tasks-load-error",
    message: "Failed to load your tasks",
  },
  projectLocations: {
    key: "developer-dashboard-project-locations-load-error",
    message: "Failed to load project locations",
  },
};

export const useDashboard = (): UseDashboardReturnData => {
  const { currentDeveloperUuid, currentUserType, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [phaseChartData, setPhaseChartData] = useState<ChartData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [unitMeasurementTotals, setUnitMeasurementTotals] = useState<InventoryBreakdownMeasurement[]>([]);
  /*
    Tracked separately from the dashboard's own `isLoading`, which is cleared by the project-phase
    request: that can land before this one, and the card would otherwise announce "no units" while
    the aggregate was still in flight.
  */
  const [isLoadingUnitMeasurementTotals, setIsLoadingUnitMeasurementTotals] = useState(true);
  /* Lives only as long as the page is open — not persisted, and local to this dashboard. */
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    ProjectIssuanceConstants.ISSUANCE_STATUS_HUB_DEFAULTS
  );
  /** Identifies the newest units fetch, so a slower earlier one cannot overwrite its figure. */
  const unitsRequestId = useRef(0);

  const [isLoadingTasks, setIsLoadingTasks] = useState(true);
  const [upcomingMilestones, setUpcomingMilestones] = useState<UpcomingMilestonePropsWithKey[]>([]);
  const [isLoadingMilestones, setIsLoadingMilestones] = useState(true);
  const [hasMilestonesError, setHasMilestonesError] = useState(false);
  /** Identifies the newest milestone fetch, so a slower earlier one cannot overwrite its result. */
  const milestonesRequestId = useRef(0);
  const [isUpcomingMilestonesPanelOpen, setIsUpcomingMilestonesPanelOpen] = useState(false);
  const [isYourTasksPanelOpen, setIsYourTasksPanelOpen] = useState(false);
  /* Drives the dashboard layout as well as the map: expanded, the map takes the whole row
     and the tasks card reflows underneath it */
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [isTasks, hasTasks] = useState(false);
  const [selectedPanelTask, setSelectedPanelTask] = useState<SearchWorkflowTasksResponse["results"][0]>();
  const [showWorkflowTaskPanel, setShowWorkflowTaskPanel] = useState(false);
  const [refreshTasks, setRefreshTasks] = useState(false);

  const handleCloseWorkflowTaskPanel = useCallback((): void => {
    setSelectedPanelTask(undefined);
    setShowWorkflowTaskPanel(false);
  }, []);

  const handleWorkflowLoadError = useCallback((): void => {
    Toast.error(LOAD_ERROR_TOASTS.taskBoard);
  }, []);

  const workflow = useTaskPanelWorkflow(selectedPanelTask?.workflowUuid, handleWorkflowLoadError);

  const handleProjectPinsLoadError = useCallback((): void => {
    Toast.error(LOAD_ERROR_TOASTS.projectLocations);
  }, []);

  const { projectPins, isLoadingProjectPins } = useProjectMapPins(currentUserType, handleProjectPinsLoadError);

  const handleRefresh = useCallback((): void => {
    setRefreshTasks((prev) => !prev);
  }, []);

  const { filterBy, filterValue } = useMemo(() => {
    if (currentDeveloperUuid) {
      return {
        filterBy: "developer",
        filterValue: currentDeveloperUuid,
      };
    }
    throw new Error("Organisation not currently linked to a developer");
  }, [currentDeveloperUuid]);

  /*
    The tiles are refetched whenever the status selection changes: every figure on the card counts
    only the selected statuses, so the whole set is rebuilt rather than patched.
  */
  const fetchUnitsData = useCallback(async () => {
    /*
      The status chips are quick to click, so two fetches can overlap. Only the newest may touch the
      card: an older one landing last would leave a figure that does not match the selection.
    */
    unitsRequestId.current += 1;
    const requestId = unitsRequestId.current;
    const isCurrentRequest = (): boolean => requestId === unitsRequestId.current;
    setIsLoadingUnitMeasurementTotals(true);

    try {
      const response = await getAggregateIssuances({
        filters: [
          /*
            No selection means no status filter, which the server reads as every status - the card
            then shows the organisation's whole inventory rather than nothing. An empty `in` list is
            rejected outright, so the entry has to be absent rather than empty.
          */
          ...(selectedStatuses.length > 0
            ? [
                {
                  filterBy: "issuanceStatus",
                  filterOperator: "in" as FilterOperator,
                  filterValue: `[${selectedStatuses.join(",")}]`,
                },
              ]
            : []),
        ],
        groupBy: ["unitMeasurement"],
        isReadableProject: true,
        paging: { beforeCursor: null, afterCursor: null, limit: 100 },
        sort: [],
        filter: { results: {} },
      });

      if (response.status !== Status.Success || !response.data) {
        if (isCurrentRequest()) Toast.error(LOAD_ERROR_TOASTS.projectOverview);
        return;
      }

      if (!isCurrentRequest()) {
        return;
      }

      setUnitMeasurementTotals(buildInventoryBreakdownMeasurements(response.data.results));
    } catch {
      if (isCurrentRequest()) Toast.error(LOAD_ERROR_TOASTS.projectOverview);
    } finally {
      // Whatever happened, the card stops saying "Loading..." - a failure is reported by the Toast.
      if (isCurrentRequest()) setIsLoadingUnitMeasurementTotals(false);
    }
  }, [selectedStatuses]);

  const fetchData = useCallback(async () => {
    try {
      const phaseAggregateResponse = await getProjectAggregate({
        filterBy,
        filterOperator: "eq",
        filterValue,
        groupBy: "cachedPhase",
        aggregation: "count",
        aggregate: "id",
      });

      if (phaseAggregateResponse.status !== Status.Success) {
        Toast.error(LOAD_ERROR_TOASTS.projectOverview);
      }

      // If no data initialise with 0
      if (phaseAggregateResponse.status === Status.Success && phaseAggregateResponse.data) {
        setPhaseChartData(getProjectPhaseCounts(phaseAggregateResponse.data));
      }
    } catch {
      Toast.error(LOAD_ERROR_TOASTS.projectOverview);
    }
  }, []);

  useEffect(() => {
    fetchData().finally(() => {
      setIsLoading(false);
    });
  }, [fetchData]);

  /*
    Separate from the effect above, and deliberately not tied to `isLoading`: a status change must
    refetch only the units figure, leaving the rest of the dashboard on screen rather than dropping
    the whole row back into its loading skeleton.
  */
  useEffect(() => {
    fetchUnitsData();
  }, [fetchUnitsData]);

  const fetchMilestones = useCallback(async () => {
    // Completing a task toggles `refreshTasks` mid-flight, so two fetches can overlap. Only the
    // newest may touch milestone state: an older one landing last would otherwise overwrite fresh
    // cards, or flag an error over results that loaded fine.
    milestonesRequestId.current += 1;
    const requestId = milestonesRequestId.current;
    const isCurrentRequest = (): boolean => requestId === milestonesRequestId.current;

    setIsLoadingMilestones(true);
    setHasMilestonesError(false);
    try {
      const result = await searchWorkflowTasks({
        paging: {
          limit: 4,
          afterCursor: null,
          beforeCursor: null,
        },
        filter: {
          results: {
            isMilestone: {
              operator: "eq",
              value: true,
            },
            deadlineDate: {
              operator: "neq",
              value: null,
            },
            status: {
              operator: "neq",
              value: TaskConstants.TASK_STATUS_COMPLETE,
            },
          },
        },
        sort: [{ key: "deadlineDate" as "results.deadlineDate", direction: "asc" }],
      });
      if (!isCurrentRequest()) {
        return;
      }
      if (result.status !== Status.Success || result.data === undefined) {
        setHasMilestonesError(true);
        Toast.error(LOAD_ERROR_TOASTS.milestones);
        return;
      }
      setUpcomingMilestones(
        result.data.results.map((task) => ({
          key: task.uuid,
          name: task.name,
          deadlineDate: task.deadlineDate ? task.deadlineDate : undefined,
          status: task.status,
          assigneeName: task.assignee?.name ?? undefined,
          /*
            The uniform `parentObject*` fields rather than `workflowObject*`: they span Project,
            Deal and Landholding and are set for general milestone tasks too, and taking the name
            and the link from the same field keeps the label and its destination in step.
          */
          project: task.parentObjectName ?? undefined,
          onProjectClick: getTaskParentObjectNavigation(
            task.parentObjectType,
            task.parentObjectUuid,
            currentUserType,
            navigate,
            { goBackText: "Back to dashboard", from: location.pathname }
          ),
          onClick: () => {
            setSelectedPanelTask(task);
            setShowWorkflowTaskPanel(true);
          },
        }))
      );
    } catch {
      if (isCurrentRequest()) {
        setHasMilestonesError(true);
        Toast.error(LOAD_ERROR_TOASTS.milestones);
      }
    } finally {
      if (isCurrentRequest()) {
        setIsLoadingMilestones(false);
      }
    }
  }, [currentUserType, navigate, location.pathname]);

  useEffect(() => {
    fetchMilestones();
  }, [fetchMilestones, refreshTasks]);

  const formatTaskData = useCallback(
    (responseData: SearchWorkflowTasksResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        result.taskLink = {
          text: el.name,
          action: () => {
            setSelectedPanelTask(el);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        /*
          The uniform parent, so the name and its link always refer to the same object — and general
          tasks, which have no `workflowObject*`, get one too. Without an action the formatter renders
          plain text, which is right for a parent this role cannot open.
        */
        result.parentObjectLink = {
          text: el.parentObjectName ?? "",
          action: getTaskParentObjectNavigation(el.parentObjectType, el.parentObjectUuid, currentUserType, navigate, {
            goBackText: "Back to dashboard",
            from: location.pathname,
          }),
        } as DataGridButtonLinkCellFormatterData;

        return result;
      }) || [],
    [currentUserType, navigate, location.pathname]
  );

  const tasksDataLoad: DataGridCursorDataLoadEventHandler = async ({ paging, filtering }: CursorChangeProps) => {
    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: 4,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
    setIsLoadingTasks(true);

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    filterCriteria.assignee = {
      uuid: {
        operator: "eq",
        value: user?.userUuid || "",
      },
    };

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };

    await searchWorkflowTasks({
      paging: {
        limit: 5,
        beforeCursor: null,
        afterCursor: null,
      },
      filter: { results: filterCriteria },
      sort: [
        {
          key: "deadlineDate" as "results.deadlineDate",
          direction: "asc",
        },
      ],
    })
      .then((response) => {
        if (response.status !== Status.Success) {
          hasTasks(false);
          Toast.error(LOAD_ERROR_TOASTS.tasks);
          return;
        }

        const formatted = formatTaskData(response.data);
        // Set isTasks to true if there are any tasks, false otherwise
        hasTasks(formatted.length > 0);

        data = {
          resultData: formatted,
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            hasNextPage: false,
            hasPreviousPage: false,
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || response.data?.results?.length || 0,
          },
        };
      })
      .catch(() => {
        hasTasks(false);
        Toast.error(LOAD_ERROR_TOASTS.tasks);
      })
      .finally(() => {
        setIsLoadingTasks(false);
      });
    return data;
  };

  return {
    phaseChartData,
    isLoading,
    currentUserType,
    unitMeasurementTotals,
    isLoadingUnitMeasurementTotals,
    selectedStatuses,
    setSelectedStatuses,

    isLoadingTasks,
    columnsTasks,
    tasksDataLoad,
    upcomingMilestones,
    isLoadingMilestones,
    hasMilestonesError,
    isUpcomingMilestonesPanelOpen,
    setIsUpcomingMilestonesPanelOpen,
    isYourTasksPanelOpen,
    setIsYourTasksPanelOpen,
    isTasks,
    projectPins,
    isLoadingProjectPins,
    isMapExpanded,
    setIsMapExpanded,
    workflow,
    selectedPanelTask,
    showWorkflowTaskPanel,
    setSelectedPanelTask,
    setShowWorkflowTaskPanel,
    handleCloseWorkflowTaskPanel,
    handleRefresh,
    refreshTasks,
  };
};
